import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft,
  Factory,
  TrendingUp,
  Package,
  Calendar,
  Users,
  Clock,
  Activity,
  ExternalLink
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// 统一的状态配置系统 - 参考 Factory.tsx 的 Badge 样式
const STATUS_CONFIG = {
  production: {
    running: { 
      label: "运行中", 
      variant: "secondary" as const, 
      color: "bg-green-500",
      badgeClass: "bg-green-100 text-green-700 border-green-200"
    },
    waiting: { 
      label: "待生产", 
      variant: "secondary" as const, 
      color: "bg-yellow-500",
      badgeClass: "bg-yellow-100 text-yellow-700 border-yellow-200"
    },
    maintenance: { 
      label: "维护中", 
      variant: "secondary" as const, 
      color: "bg-gray-500",
      badgeClass: "bg-gray-100 text-gray-700 border-gray-200"
    }
  },
  inventory: {
    sufficient: { 
      label: "完备", 
      variant: "secondary" as const, 
      color: "bg-green-500",
      badgeClass: "bg-green-100 text-green-700 border-green-200"
    },
    adequate: { 
      label: "充足", 
      variant: "secondary" as const, 
      color: "bg-blue-500",
      badgeClass: "bg-blue-100 text-blue-700 border-blue-200"
    },
    low: { 
      label: "不足", 
      variant: "secondary" as const, 
      color: "bg-red-500",
      badgeClass: "bg-red-100 text-red-700 border-red-200"
    }
  },
  order: {
    inProgress: { 
      label: "生产中", 
      variant: "secondary" as const, 
      color: "bg-green-500",
      badgeClass: "bg-green-100 text-green-700 border-green-200"
    },
    pending: { 
      label: "待生产", 
      variant: "secondary" as const, 
      color: "bg-yellow-500",
      badgeClass: "bg-yellow-100 text-yellow-700 border-yellow-200"
    },
    waiting: { 
      label: "待处理", 
      variant: "secondary" as const, 
      color: "bg-gray-500",
      badgeClass: "bg-gray-100 text-gray-700 border-gray-200"
    }
  }
};

// 统一的卡片组件样式 - 参考 Factory.tsx
const CARD_STYLES = {
  base: "hover:shadow-lg transition-all duration-300 bg-white border-slate-200 cursor-pointer",
  baseNonClickable: "hover:shadow-md transition-all duration-300 bg-white border-slate-200",
  header: "pb-3",
  content: "space-y-3 p-4",
  contentCompact: "p-3",
  title: "text-16 font-semibold text-slate-900", // 16px 主标题
  subtitle: "text-14 text-slate-600", // 14px 正文
  caption: "text-12 text-slate-500", // 12px 辅助信息
};

// 统一的图标样式
const ICON_STYLES = {
  section: "h-5 w-5 mr-2 text-primary",
  card: "h-4 w-4 mr-2 text-slate-500",
  cardSmall: "h-3 w-3 mr-1 text-slate-400",
  status: "h-3 w-3 rounded-full",
};

const FactoryDetails = () => {
  const navigate = useNavigate();

  const productionLines = [
    {
      id: "line-1",
      name: "生产线1",
      status: "running" as keyof typeof STATUS_CONFIG.production,
      product: "A型号电机",
      progress: 85,
      operator: "张师傅",
      startTime: "08:00",
      estimatedCompletion: "16:30"
    },
    {
      id: "line-2", 
      name: "生产线2",
      status: "waiting" as keyof typeof STATUS_CONFIG.production,
      product: "B型号电机",
      progress: 0,
      operator: "李师傅",
      startTime: "14:00",
      estimatedCompletion: "22:00"
    },
    {
      id: "line-3",
      name: "生产线3",
      status: "running" as keyof typeof STATUS_CONFIG.production,
      product: "B型号电机",
      progress: 45,
      operator: "王师傅", 
      startTime: "06:00",
      estimatedCompletion: "18:00"
    }
  ];

  const inventoryItems = [
    {
      id: "A001",
      name: "A型号原料",
      orderNumber: "TO001",
      quantity: 1000,
      unit: "件",
      supplier: "中立商A",
      status: "sufficient" as keyof typeof STATUS_CONFIG.inventory,
      inboundDate: "2024-02-15",
      expiryDate: "2024-02-25"
    },
    {
      id: "B001", 
      name: "B型号成品",
      orderNumber: "TO002",
      quantity: 500,
      unit: "台",
      supplier: "上海合作",
      status: "adequate" as keyof typeof STATUS_CONFIG.inventory,
      inboundDate: "2024-02-16",
      expiryDate: "2024-02-23"
    },
    {
      id: "C001",
      name: "C型号原料", 
      orderNumber: "TO003",
      quantity: 2000,
      unit: "件",
      supplier: "中立商B",
      status: "sufficient" as keyof typeof STATUS_CONFIG.inventory,
      inboundDate: "2024-02-17",
      expiryDate: "2024-02-28"
    }
  ];

  const recentOrders = [
    {
      product: "A型号电机",
      quantity: 100,
      deliveryDate: "2024-02-20",
      status: "inProgress" as keyof typeof STATUS_CONFIG.order
    },
    {
      product: "B型号电机", 
      quantity: 50,
      deliveryDate: "2024-02-22",
      status: "pending" as keyof typeof STATUS_CONFIG.order
    },
    {
      product: "C型号电机",
      quantity: 200,
      deliveryDate: "2024-02-25", 
      status: "waiting" as keyof typeof STATUS_CONFIG.order
    },
    {
      product: "A型号电机",
      quantity: 150,
      deliveryDate: "2024-02-28",
      status: "waiting" as keyof typeof STATUS_CONFIG.order
    }
  ];

  return (
    <div className="min-h-screen bg-cream overflow-x-auto">
      {/* Header - 参考 Factory.tsx 的头部样式 */}
      <div className="bg-cream-80 border-b border-slate-200 sticky top-0 z-10 backdrop-blur-sm">
        <div className="w-full px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/factory")}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm">
                <Factory className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-16 font-bold text-slate-900">工厂详细信息</h1>
                <p className="text-12 text-slate-600 hidden sm:block">生产线状态 · 库存详情 · 订单管理</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-slate-600">
              <Calendar className="h-4 w-4" />
              <span className="text-12 hidden sm:inline">
                {new Date().toLocaleDateString('zh-CN', {
                  month: 'short',
                  day: 'numeric'
                })}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
        {/* Production Lines Status - 保持水平布局 */}
        <div className="space-y-4">
          <div className="flex items-center">
            <Activity className={ICON_STYLES.section} />
            <h2 className={CARD_STYLES.title}>生产线状态</h2>
          </div>
          
          <div className="grid gap-3 md:gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {productionLines.map((line) => {
              const statusConfig = STATUS_CONFIG.production[line.status];
              return (
                <Card key={line.id} className={CARD_STYLES.base} onClick={() => console.log('查看生产线详情', line.id)}>
                  <CardHeader className={CARD_STYLES.header}>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-14 font-semibold text-slate-900">{line.name}</CardTitle>
                      <Badge className={`${statusConfig.badgeClass} text-12`}>
                        {statusConfig.label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-12 text-slate-600">{line.product}</span>
                        <span className="text-12 font-medium text-slate-900">{line.progress}%</span>
                      </div>
                      <Progress value={line.progress} className="h-1.5" />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-12">
                      <div className="flex items-center">
                        <Users className={ICON_STYLES.cardSmall} />
                        <span className="text-slate-600">{line.operator}</span>
                      </div>
                      <div className="flex items-center">
                        <Clock className={ICON_STYLES.cardSmall} />
                        <span className="text-slate-600">{line.startTime}-{line.estimatedCompletion}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* 响应式左右布局区域 */}
        <div className="grid gap-4 md:gap-6 xl:grid-cols-2">
          {/* 左侧：库存详情 */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Package className={ICON_STYLES.section} />
                <h2 className={CARD_STYLES.title}>在途库存详情</h2>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate("/flow-chart")} 
                className="text-12 h-8"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                查看流程
              </Button>
            </div>

            <div className="space-y-2">
              {inventoryItems.map((item) => {
                const statusConfig = STATUS_CONFIG.inventory[item.status];
                return (
                  <Card key={item.id} className={CARD_STYLES.base} onClick={() => console.log('查看库存详情', item.id)}>
                    <CardContent className={CARD_STYLES.contentCompact}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="p-1.5 bg-primary/10 rounded-md shrink-0">
                            <Package className="h-3 w-3 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-14 font-semibold text-slate-900 truncate">{item.name}</h3>
                            <p className="text-12 text-slate-600 truncate">
                              {item.orderNumber} · {item.quantity}{item.unit} · {item.supplier}
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-right shrink-0 ml-2">
                          <Badge className={`${statusConfig.badgeClass} text-10 px-2 py-0.5 mb-1`}>
                            {statusConfig.label}
                          </Badge>
                          <div className="text-10 text-slate-500">
                            <p>{item.inboundDate.slice(5)} → {item.expiryDate.slice(5)}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* 右侧：订单状态 */}
          <div className="space-y-4">
            <div className="flex items-center">
              <TrendingUp className={ICON_STYLES.section} />
              <h2 className={CARD_STYLES.title}>订单状态</h2>
            </div>

            <Card className={CARD_STYLES.baseNonClickable}>
              <CardHeader className={CARD_STYLES.header}>
                <CardTitle className="text-16 font-semibold text-slate-900">在制订单</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {recentOrders.map((order, index) => {
                  const statusConfig = STATUS_CONFIG.order[order.status];
                  return (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-2.5 rounded-md bg-slate-50 hover:bg-slate-100 transition-colors duration-200 cursor-pointer"
                      onClick={() => console.log('查看订单详情', order.product)}
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className={`${ICON_STYLES.status} ${statusConfig.color} shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-14 font-medium text-slate-900 truncate">{order.product}</p>
                          <p className="text-12 text-slate-600">数量: {order.quantity}</p>
                        </div>
                      </div>
                      
                      <div className="text-right shrink-0 ml-2">
                        <Badge className={`${statusConfig.badgeClass} text-10 px-2 py-0.5 mb-1`}>
                          {statusConfig.label}
                        </Badge>
                        <p className="text-10 text-slate-500">
                          {order.deliveryDate.slice(5)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FactoryDetails;